export type CourseProgressLesson = {
  id: string;
  freePreview?: boolean;
  lockUntilPreviousComplete?: boolean;
  prerequisiteLessonIds?: unknown;
};

export type CourseProgressModule = {
  lessons?: CourseProgressLesson[];
};

export type CourseLessonProgressSnapshot = {
  completed: boolean;
  lastPositionSeconds?: number | null;
};

export type CourseLessonAccessState = {
  accessible: boolean;
  locked: boolean;
  dripLocked?: boolean;
  missingPrerequisiteLessonIds?: string[];
  unlockAt?: string | null;
  completed: boolean;
  lastPositionSeconds?: number | null;
};

export type BuildLessonAccessOptions = {
  /** Progress mutations enforce drip/sequential locks even on free-preview lessons. */
  respectLocksForFreePreview?: boolean;
};

export function flattenPublishedLessons(modules: CourseProgressModule[]): CourseProgressLesson[] {
  return modules.flatMap((module) => module.lessons ?? []);
}

export function buildLessonAccessStates(
  flatLessons: CourseProgressLesson[],
  progressByLesson: Record<string, CourseLessonProgressSnapshot>,
  entitled: boolean,
  options?: BuildLessonAccessOptions,
): Record<string, CourseLessonAccessState> {
  const states: Record<string, CourseLessonAccessState> = {};
  let previousCompleted = true;

  for (const lesson of flatLessons) {
    const snapshot = progressByLesson[lesson.id];
    const completed = snapshot?.completed ?? false;
    const lastPositionSeconds = snapshot?.lastPositionSeconds ?? null;

    if (lesson.freePreview && !options?.respectLocksForFreePreview) {
      states[lesson.id] = {
        accessible: true,
        locked: false,
        completed: entitled ? completed : false,
        lastPositionSeconds: entitled ? lastPositionSeconds : null,
      };
      if (entitled) {
        previousCompleted = completed;
      }
      continue;
    }

    if (!entitled) {
      states[lesson.id] = {
        accessible: false,
        locked: false,
        completed: false,
        lastPositionSeconds: null,
      };
      continue;
    }

    const prerequisiteLessonIds = Array.isArray(lesson.prerequisiteLessonIds)
      ? lesson.prerequisiteLessonIds.filter((id): id is string => typeof id === 'string')
      : [];
    const missingPrerequisiteLessonIds = prerequisiteLessonIds.filter(
      (prerequisiteLessonId) => !progressByLesson[prerequisiteLessonId]?.completed,
    );
    const blockedByLock = Boolean(lesson.lockUntilPreviousComplete) && !previousCompleted;
    const blockedByPrerequisites = missingPrerequisiteLessonIds.length > 0;
    states[lesson.id] = {
      accessible: !blockedByLock && !blockedByPrerequisites,
      locked: blockedByLock || blockedByPrerequisites,
      missingPrerequisiteLessonIds,
      completed,
      lastPositionSeconds,
    };
    previousCompleted = completed;
  }

  return states;
}

export function computeCourseProgressPercent(
  flatLessons: CourseProgressLesson[],
  progressByLesson: Record<string, CourseLessonProgressSnapshot>,
): number {
  if (flatLessons.length === 0) return 0;
  const completedCount = flatLessons.filter((lesson) => progressByLesson[lesson.id]?.completed).length;
  return Math.round((completedCount / flatLessons.length) * 100);
}

export function resolveContinueLesson(
  flatLessons: CourseProgressLesson[],
  accessStates: Record<string, CourseLessonAccessState>,
  progressByLesson: Record<string, CourseLessonProgressSnapshot>,
): { lessonId: string | null; lastPositionSeconds: number | null } {
  for (const lesson of flatLessons) {
    const access = accessStates[lesson.id];
    const progress = progressByLesson[lesson.id];
    if (!access?.accessible) continue;
    if (!progress?.completed) {
      return {
        lessonId: lesson.id,
        lastPositionSeconds: progress?.lastPositionSeconds ?? access.lastPositionSeconds ?? null,
      };
    }
  }

  const firstAccessible = flatLessons.find((lesson) => accessStates[lesson.id]?.accessible);
  return {
    lessonId: firstAccessible?.id ?? flatLessons[0]?.id ?? null,
    lastPositionSeconds: null,
  };
}

export function isCourseFullyComplete(
  flatLessons: CourseProgressLesson[],
  progressByLesson: Record<string, CourseLessonProgressSnapshot>,
): boolean {
  if (flatLessons.length === 0) return false;
  return flatLessons.every((lesson) => progressByLesson[lesson.id]?.completed);
}
